/**
 * GTN Product Shop — frontend interactive filtering.
 *
 * Uses the WooCommerce Store API (/wc/store/v1/products) to update the
 * product grid when the user searches or changes the category filter.
 *
 * The initial render is done server-side (render.php) for performance and
 * SEO. This script only makes network requests when a filter is changed.
 */
( function () {
	'use strict';

	// ── Helpers ────────────────────────────────────────────────────────

	/**
	 * Minimal HTML entity escaper — safe for use inside innerHTML templates.
	 *
	 * @param {*} str
	 * @return {string}
	 */
	function esc( str ) {
		const el = document.createElement( 'div' );
		el.textContent = String( str == null ? '' : str );
		return el.innerHTML;
	}

	/**
	 * Validate and sanitize a URL returned by the API.
	 *
	 * @param {string} str
	 * @return {string}
	 */
	function safeUrl( str ) {
		try {
			const u = new URL( String( str ) );
			return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : '#';
		} catch {
			return '#';
		}
	}

	/**
	 * Format a WC Store API price string (integer subunits) to a decimal string.
	 *
	 * @param {string|number} amountStr
	 * @param {number}        minorUnit Decimal places (usually 2).
	 * @return {string}
	 */
	function formatPrice( amountStr, minorUnit ) {
		const decimals = Number.isFinite( minorUnit ) ? minorUnit : 2;
		const divisor = Math.pow( 10, decimals );
		return ( parseInt( amountStr, 10 ) / divisor ).toFixed( decimals );
	}

	/**
	 * Build an HTML product card from a WC Store API product object.
	 *
	 * All text and URLs are escaped before insertion.
	 *
	 * @param {Object} product
	 * @return {string}
	 */
	function productCardHTML( product, labels ) {
		const prices  = product.prices || {};
		const prefix  = esc( prices.currency_prefix  || prices.currency_symbol || '' );
		const suffix  = esc( prices.currency_suffix  || '' );
		const minor   = prices.currency_minor_unit;

		let priceHTML;
		if ( product.on_sale && prices.regular_price !== prices.sale_price ) {
			priceHTML =
				'<div class="gtn-product-card__price">' +
				'<del>' + prefix + formatPrice( prices.regular_price, minor ) + suffix + '</del>' +
				'<ins>' + prefix + formatPrice( prices.sale_price,    minor ) + suffix + '</ins>' +
				'</div>';
		} else {
			priceHTML =
				'<div class="gtn-product-card__price">' +
				prefix + formatPrice( prices.price, minor ) + suffix +
				'</div>';
		}

		const badge = product.on_sale
			? '<span class="gtn-product-card__badge">' + esc( labels.sale ) + '</span>'
			: '';

		const firstImg = product.images && product.images[ 0 ];
		const image    = firstImg
			? '<img src="' + safeUrl( firstImg.src ) + '" alt="' + esc( firstImg.alt || product.name ) + '" loading="lazy" width="600" height="600">'
			: '<div class="gtn-product-card__no-img" aria-hidden="true"></div>';

		const url  = safeUrl( product.permalink );
		const name = esc( product.name );

		return (
			'<article class="gtn-product-card">' +
				'<a href="' + url + '" class="gtn-product-card__media" tabindex="-1" aria-hidden="true">' +
					image + badge +
				'</a>' +
				'<div class="gtn-product-card__body">' +
					'<h3 class="gtn-product-card__title"><a href="' + url + '">' + name + '</a></h3>' +
					priceHTML +
					'<a href="' + url + '" class="gtn-product-card__btn">' + esc( labels.viewProduct ) + '</a>' +
				'</div>' +
			'</article>'
		);
	}

	// ── URL & History Management ──────────────────────────────────────

	/**
	 * Parse current URL parameters for search, category, and page.
	 *
	 * @return {Object} Parsed parameters { search, category, page }
	 */
	function parseUrlParams() {
		const params = new URLSearchParams( window.location.search );
		return {
			search:   params.get( 'search' )     || '',
			category: params.get( 'product_cat' ) || '',
			page:     parseInt( params.get( 'shop_page' ) || '1', 10 ),
		};
	}

	/**
	 * Update the browser URL to reflect current filter state.
	 *
	 * @param {Object} state The current filter state { search, category, page, ... }
	 */
	function updateUrl( state ) {
		const params = new URLSearchParams();
		if ( state.search )   params.set( 'search', state.search );
		if ( state.category ) params.set( 'product_cat', state.category );
		if ( state.page > 1 ) params.set( 'shop_page', state.page );

		const queryStr = params.toString();
		const url      = queryStr ? window.location.pathname + '?' + queryStr : window.location.pathname;

		window.history.pushState( { page: state.page }, '', url );
	}

	// ── Core ───────────────────────────────────────────────────────────

	function initShop( root ) {
		const perPage  = parseInt( root.dataset.perPage, 10 )  || 12;
		const apiRoot  = root.dataset.apiRoot || '/wp-json/';
		const apiUrl   = apiRoot.replace( /\/$/, '' ) + '/wc/store/v1/products';

		// Parse URL parameters to restore filter state.
		const urlParams = parseUrlParams();

		// State — initialised from URL params (if present) or server-rendered data attributes.
		const state = {
			search:   urlParams.search   || root.dataset.initSearch   || '',
			category: urlParams.category || root.dataset.initCat      || '',
			page:     urlParams.page     || parseInt( root.dataset.initPage || '1', 10 ) || 1,
			perPage,
			total:    parseInt( root.dataset.total, 10 ) || 0,
			pages:    1,
			loading:  false,
		};

		const labels = {
			sale: root.dataset.labelSale || '',
			viewProduct: root.dataset.labelViewProduct || '',
			productSingular: root.dataset.labelProductSingular || '',
			productPlural: root.dataset.labelProductPlural || '',
			prevPage: root.dataset.labelPrevPage || '',
			nextPage: root.dataset.labelNextPage || '',
			noProducts: root.dataset.labelNoProducts || '',
		};

		// DOM refs.
		const searchInput  = root.querySelector( '[data-gtn-search]' );
		const catSelect    = root.querySelector( '[data-gtn-category]' );
		const grid         = root.querySelector( '[data-gtn-grid]' );
		const paginationEl = root.querySelector( '[data-gtn-pagination]' );
		const countEl      = root.querySelector( '[data-gtn-count]' );
		const loadingEl    = root.querySelector( '[data-gtn-loading]' );

		// ── Renderers ────────────────────────────────────────────────

		function setLoading( on ) {
			state.loading = on;
			if ( loadingEl ) loadingEl.setAttribute( 'aria-hidden', on ? 'false' : 'true' );
			if ( grid )      grid.style.opacity = on ? '0.4' : '1';
		}

		function renderCount( total ) {
			if ( ! countEl ) return;
			countEl.textContent = total === 1 ? '1 ' + labels.productSingular : total + ' ' + labels.productPlural;
		}

		function renderPagination( totalPages ) {
			if ( ! paginationEl ) return;
			if ( totalPages <= 1 ) {
				paginationEl.innerHTML = '';
				return;
			}
			const items = [];

			if ( state.page > 1 ) {
				items.push(
					'<button class="gtn-shop__page-btn gtn-shop__page-btn--prev" data-page="' + ( state.page - 1 ) + '" aria-label="' + esc( labels.prevPage ) + '">' +
					'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m15 18-6-6 6-6"/></svg>' +
					'</button>'
				);
			}

			for ( let p = 1; p <= totalPages; p++ ) {
				const active = p === state.page;
				items.push(
					'<button class="gtn-shop__page-btn' + ( active ? ' is-active' : '' ) + '"' +
					( active ? ' aria-current="page"' : '' ) +
					' data-page="' + p + '">' + p + '</button>'
				);
			}

			if ( state.page < totalPages ) {
				items.push(
					'<button class="gtn-shop__page-btn gtn-shop__page-btn--next" data-page="' + ( state.page + 1 ) + '" aria-label="' + esc( labels.nextPage ) + '">' +
					'<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="m9 18 6-6-6-6"/></svg>' +
					'</button>'
				);
			}

			paginationEl.innerHTML = '<div class="gtn-shop__pages">' + items.join( '' ) + '</div>';
		}

		// ── Fetching ─────────────────────────────────────────────────

		async function fetchProducts() {
			if ( state.loading ) return;
			setLoading( true );

			const params = new URLSearchParams( {
				per_page: String( state.perPage ),
				page:     String( state.page ),
				orderby:  'date',
				order:    'desc',
			} );
			if ( state.search )   params.set( 'search',   state.search );
			if ( state.category ) params.set( 'category', state.category );

			try {
				const response = await fetch( apiUrl + '?' + params.toString(), {
					method:  'GET',
					headers: { 'Content-Type': 'application/json' },
					cache:   'no-store',
				} );

				if ( ! response.ok ) {
					throw new Error( 'Store API returned ' + response.status );
				}

				const products = await response.json();
				const total    = parseInt( response.headers.get( 'X-WP-Total' )      || '0', 10 );
				const pages    = parseInt( response.headers.get( 'X-WP-TotalPages' ) || '1', 10 );

				state.total = total;
				state.pages = pages;

				if ( grid ) {
					grid.innerHTML = products.length
						? products.map( function ( product ) {
							return productCardHTML( product, labels );
						} ).join( '' )
						: '<p class="gtn-shop__empty">' + esc( labels.noProducts ) + '</p>';
				}

				renderPagination( pages );
				renderCount( total );
			} catch ( err ) {
				// eslint-disable-next-line no-console
				console.error( 'GTN Shop fetch error:', err );
			} finally {
				setLoading( false );
			}
		}

		// ── Events ───────────────────────────────────────────────────

		let debounceTimer;

		if ( searchInput ) {
			searchInput.addEventListener( 'input', function ( e ) {
				clearTimeout( debounceTimer );
				debounceTimer = setTimeout( function () {
					const newSearch = e.target.value.trim();
					if ( newSearch === state.search ) return;
					state.search = newSearch;
					state.page   = 1;
					updateUrl( state );
					fetchProducts();
				}, 450 );
			} );
		}

		if ( catSelect ) {
			catSelect.addEventListener( 'change', function ( e ) {
				const newCat = e.target.value;
				if ( newCat === state.category ) return;
				state.category = newCat;
				state.page     = 1;
				updateUrl( state );
				fetchProducts();
			} );
		}

		if ( paginationEl ) {
			paginationEl.addEventListener( 'click', function ( e ) {
				const btn = e.target.closest( '[data-page]' );
				if ( ! btn || state.loading ) return;
				const nextPage = parseInt( btn.dataset.page, 10 );
				if ( nextPage === state.page ) return;
				state.page = nextPage;
				updateUrl( state );
				fetchProducts();
				root.scrollIntoView( { behavior: 'smooth', block: 'start' } );
			} );
		}

		// ── Hydrate initial pagination from server state ──────────────

		const initPages = Math.ceil( state.total / state.perPage );
		if ( initPages > 1 ) {
			state.pages = initPages;
			renderPagination( initPages );
		}

		// ── Handle browser back/forward ────────────────────────────────

		window.addEventListener( 'popstate', function () {
			const urlState = parseUrlParams();
			const didChange = 
				urlState.search !== state.search ||
				urlState.category !== state.category ||
				urlState.page !== state.page;

			if ( ! didChange ) return;

			state.search   = urlState.search;
			state.category = urlState.category;
			state.page     = urlState.page;

			// Update UI to match restored state.
			if ( searchInput ) searchInput.value = state.search;
			if ( catSelect ) catSelect.value = state.category;

			fetchProducts();
		} );
	}

	// ── Bootstrap ──────────────────────────────────────────────────────

	document.querySelectorAll( '[data-gtn-shop]' ).forEach( initShop );
} )();
